/**
 * <aa-pdp-rail> — desktop product gallery controls, modelled on nude-project.com's product page.
 *
 * Wraps the vertical progress rail (snippets/aa-media-progress.liquid) inside the gallery's
 * slideshow-component, rendered by product-media-gallery-content when the desktop vertical
 * gallery is on. The images themselves are a plain scroll box (snippets/aa-pdp-gallery-styles),
 * so the wheel over them is the browser's own scroll. This adds the rest:
 *
 * - hovering a thumbnail shows that image (as clicking does);
 * - the wheel over the thumbnails or the rail scrolls the images, 1:1, and once the images are
 *   at their first or last one it is left alone so the page scrolls instead;
 * - when scrolling changes the image, the active thumbnail is kept in view in a long strip.
 *
 * Desktop with a mouse or trackpad only: (min-width: 750px) and (hover: hover).
 */
const desktop = window.matchMedia('(min-width: 750px) and (hover: hover)');

/** Pixels per wheel "line" (Firefox reports lines). */
const LINE_HEIGHT = 40;

/** After a wheel event, ignore hover for this long: the strip may move under a still pointer. */
const HOVER_QUIET_MS = 400;

class AAPdpRail extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  #lastWheel = 0;

  connectedCallback() {
    const slideshow = /** @type {HTMLElement & { select?: Function, current?: number } | null} */ (
      this.closest('slideshow-component')
    );
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (!slideshow || !(scroller instanceof HTMLElement)) return;

    const controls = slideshow.querySelector(':scope > slideshow-controls[thumbnails]');
    const strip = controls?.querySelector('.slideshow-controls__thumbnails-container');
    const thumbs = () => [...(controls?.querySelectorAll('.slideshow-controls__thumbnail') ?? [])];

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    /** @param {WheelEvent} event */
    const onWheel = (event) => {
      if (!desktop.matches || event.ctrlKey) return; // ctrl + wheel is pinch zoom
      let delta = event.deltaY;
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) delta *= LINE_HEIGHT;
      else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) delta *= scroller.clientHeight;
      if (!delta || Math.abs(delta) < Math.abs(event.deltaX)) return;

      const max = scroller.scrollHeight - scroller.clientHeight;
      const atEdge = delta > 0 ? scroller.scrollTop >= max - 1 : scroller.scrollTop <= 0;
      if (atEdge) return; // let the page scroll, as the images themselves do

      event.preventDefault();
      this.#lastWheel = performance.now();
      scroller.scrollBy({ top: delta, behavior: 'instant' });
    };

    for (const target of [this, controls]) {
      target?.addEventListener('wheel', /** @type {EventListener} */ (onWheel), { passive: false, signal });
    }

    controls?.addEventListener(
      'pointerover',
      (event) => {
        if (!desktop.matches || event.pointerType !== 'mouse') return;
        if (performance.now() - this.#lastWheel < HOVER_QUIET_MS) return;
        const thumb = event.target instanceof Element ? event.target.closest('.slideshow-controls__thumbnail') : null;
        if (!thumb) return;
        const index = thumbs().indexOf(thumb);
        if (index < 0 || index === slideshow.current || typeof slideshow.select !== 'function') return;
        slideshow.select(index, event);
      },
      { signal }
    );

    // The theme centres the active thumbnail on clicks only; scrolling the images can leave it
    // outside a long strip, so bring it to the nearest edge.
    slideshow.addEventListener(
      'slideshow:select',
      (event) => {
        if (event.target !== slideshow || !(strip instanceof HTMLElement)) return;
        const { index } = /** @type {CustomEvent} */ (event).detail ?? {};
        const thumb = thumbs()[index];
        if (!(thumb instanceof HTMLElement)) return;
        const box = strip.getBoundingClientRect();
        const rect = thumb.getBoundingClientRect();
        if (rect.top < box.top) strip.scrollTop -= box.top - rect.top;
        else if (rect.bottom > box.bottom) strip.scrollTop += rect.bottom - box.bottom;
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }
}

if (!customElements.get('aa-pdp-rail')) {
  customElements.define('aa-pdp-rail', AAPdpRail);
}
